import { useDateTimeDisplay } from '../../util/useDateTimeDisplay';
import DateTimePreferencesLink from '../../util/DateTimePreferencesLink';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DirectionsIcon from "@mui/icons-material/Directions";
import GoogleIcon from "@mui/icons-material/Google";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShareIcon from "@mui/icons-material/Share";
import { useParams } from "react-router-dom";
import { getApiBaseUrl } from "../../util/apiBaseUrl";
import { getToken } from "../../util/authUtils";
import SeoMeta from "../../components/common/seoMeta";

const REMINDER_OPTIONS = [
  { value: 0, label: "At event time" },
  { value: 10, label: "10 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
  { value: 1440, label: "1 day before" },
  { value: 10080, label: "1 week before" },
];

const CAPTURE_INVITE_CARD_TEXT = "Tap to open the interactive poster.";
const LEGACY_CUSTOM_LINKS_TITLE = "Links from this Timely365 page";


function formatDisplayTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const amPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)$/i);
  if (amPm) return `${Number(amPm[1])}:${String(Number(amPm[2] || 0)).padStart(2, "0")} ${amPm[3].replace(/\./g, "").toUpperCase()}`;
  const match = raw.match(/^(\d{1,2})(?::(\d{2}))$/);
  if (!match) return raw;
  const hour24 = Number(match[1]);
  const meridiem = hour24 >= 12 ? "PM" : "AM";
  return `${hour24 % 12 || 12}:${String(Number(match[2] || 0)).padStart(2, "0")} ${meridiem}`;
}

function parseTimeValue(value) {
  const raw = String(value || "").trim().toLowerCase();
  const amPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (amPm) {
    let hour = Number(amPm[1]);
    if (amPm[3] === "pm" && hour < 12) hour += 12;
    if (amPm[3] === "am" && hour === 12) hour = 0;
    return { hour, minute: Number(amPm[2] || 0) };
  }
  const match = raw.match(/^(\d{1,2})(?::(\d{2}))?$/);
  return match ? { hour: Number(match[1]), minute: Number(match[2] || 0) } : null;
}

function toGoogleStamp(date, time, addHours = 0) {
  const parsed = parseTimeValue(time);
  if (!date || !parsed) return null;
  const local = new Date(`${date}T${String(parsed.hour).padStart(2, "0")}:${String(parsed.minute).padStart(2, "0")}:00`);
  if (Number.isNaN(local.getTime())) return null;
  local.setHours(local.getHours() + addHours);
  return local.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function buildGoogleCalendarUrl(extracted) {
  const start = toGoogleStamp(extracted?.date, extracted?.startTime);
  const end = extracted?.endTime ? toGoogleStamp(extracted.date, extracted.endTime) : toGoogleStamp(extracted?.date, extracted?.startTime, 1);
  if (!start || !end) return null;
  const url = new URL("https://calendar.google.com/calendar/render");
  url.searchParams.set("action", "TEMPLATE");
  url.searchParams.set("text", extracted?.title || "Timely365 Invite");
  url.searchParams.set("dates", `${start}/${end}`);
  if (extracted?.location) url.searchParams.set("location", extracted.location);
  return url.toString();
}

function buildDirectionsUrl(extracted) {
  const destination = String(extracted?.address || extracted?.location || "").trim();
  if (!destination) return null;
  const url = new URL("https://www.google.com/maps/dir/");
  url.searchParams.set("api", "1");
  url.searchParams.set("destination", destination);
  return url.toString();
}

function normalizeExternalUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function normalizeCustomLinks(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const url = normalizeExternalUrl(item?.url);
      if (!url) return null;
      let fallbackLabel = "Open link";
      try {
        fallbackLabel = new URL(url).hostname.replace(/^www\./i, "") || fallbackLabel;
      } catch (_error) {
        fallbackLabel = "Open link";
      }
      return {
        label: String(item?.label || "").trim() || fallbackLabel,
        url,
      };
    })
    .filter(Boolean)
    .slice(0, 8);
}

function getCustomLinksTitle(value) {
  const title = String(value || "").trim();
  return title === LEGACY_CUSTOM_LINKS_TITLE ? "" : title;
}

function normalizePublicVisibility(value) {
  const source = value && typeof value === "object" ? value : {};
  return {
    showDateTime: source.showDateTime !== false,
    showLocation: source.showLocation !== false,
  };
}

async function readJsonResponse(response, fallbackMessage) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (_error) {
    return {
      ok: false,
      error: response.ok ? fallbackMessage : text || fallbackMessage,
    };
  }
}

export default function TimelyInvitePage() {
  const { dateLabel, clockLabel } = useDateTimeDisplay();
  const { token } = useParams();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const autoSaveAttemptedRef = useRef(false);
  const [reminderMinutes, setReminderMinutes] = useState(() => {
    if (typeof window === "undefined") return 1440;
    const params = new URLSearchParams(window.location.search);
    const value = Number(params.get("reminderMinutes"));
    return REMINDER_OPTIONS.some((option) => option.value === value) ? value : 1440;
  });

  useEffect(() => {
    let active = true;
    async function loadInvite() {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${apiBaseUrl}/api/capture/public/${encodeURIComponent(token || "")}`);
        const payload = await readJsonResponse(response, "Invite service is unavailable.");
        if (!response.ok || !payload.ok) throw new Error(payload.error || "Invite not found.");
        if (active) setInvite(payload.invite);
      } catch (err) {
        if (active) setError(err.message || "Invite not found.");
      } finally {
        if (active) setLoading(false);
      }
    }
    loadInvite();
    return () => {
      active = false;
    };
  }, [apiBaseUrl, token]);

  const shareInvite = useCallback(async () => {
    const shareUrl = window.location.href;
    const title = invite?.extracted?.title || "Timely365 Invite";
    if (navigator.share) {
      await navigator.share({ title, url: shareUrl }).catch(() => {});
      return;
    }
    await navigator.clipboard?.writeText(shareUrl).catch(() => {});
  }, [invite]);

  const saveToTimely365 = useCallback(async () => {
    if (invite?.extracted?.captureType === "poster") {
      setActionError("This poster is not a calendar event.");
      return;
    }
    const authToken = getToken();
    if (!authToken) {
      const returnTo = `${window.location.pathname}?reminderMinutes=${encodeURIComponent(reminderMinutes)}&saveAfterLogin=1`;
      window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
      return;
    }
    setSaving(true);
    setActionError("");
    setNotice("");
    try {
      const response = await fetch(`${apiBaseUrl}/api/capture/public/${encodeURIComponent(token || "")}/add`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ reminderMinutes }),
      });
      const payload = await readJsonResponse(response, "Invite service is unavailable.");
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || "Could not save this invite.");
      }
      setNotice("Saved to your Timely365 calendar.");
    } catch (err) {
      setActionError(err.message || "Could not save this invite.");
    } finally {
      setSaving(false);
    }
  }, [apiBaseUrl, invite, reminderMinutes, token]);

  useEffect(() => {
    if (loading || error || notice || saving || autoSaveAttemptedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("saveAfterLogin") !== "1" || !getToken()) return;
    autoSaveAttemptedRef.current = true;
    saveToTimely365();
  }, [error, loading, notice, saveToTimely365, saving]);

  const extracted = invite?.extracted || {};
  const publicVisibility = normalizePublicVisibility(extracted.publicVisibility);
  const showDateTime = publicVisibility.showDateTime;
  const showLocation = publicVisibility.showLocation;
  const googleUrl = showDateTime ? buildGoogleCalendarUrl(extracted) : null;
  const directionsUrl = showLocation ? buildDirectionsUrl(extracted) : null;
  const hasCalendarReadyDetails = showDateTime && Boolean(String(extracted.date || "").trim() && String(extracted.startTime || "").trim());
  const isAdPoster = extracted.captureType === "poster";
  const canShowCalendarActions = hasCalendarReadyDetails && !isAdPoster;
  const canShareInvite = extracted.shareEnabled === true;
  const customLinks = normalizeCustomLinks(extracted.customLinks);
  const customLinksTitle = getCustomLinksTitle(extracted.customLinksTitle);
  const seoDescription = CAPTURE_INVITE_CARD_TEXT;
  const seoImage = token ? `${apiBaseUrl}/api/capture/public/${encodeURIComponent(token)}/poster?v=original` : undefined;

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: { xs: 2, md: 5 } }}>
      {!loading && !error && (
        <SeoMeta
          title={CAPTURE_INVITE_CARD_TEXT}
          description={seoDescription}
          image={extracted.posterImageDataUrl ? seoImage : undefined}
          imageType="image/png"
          imageWidth={null}
          imageHeight={null}
          url={typeof window !== "undefined" ? window.location.href : undefined}
          siteName="Timely365"
          type="article"
        />
      )}
      <Container maxWidth="sm">
        <DateTimePreferencesLink />
        {loading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
            <CircularProgress size={28} />
            <Typography>Loading invite...</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
            {extracted.posterImageDataUrl && (
              <Box
                component="img"
                src={extracted.posterImageDataUrl}
                alt={extracted.title || "Invitation poster"}
                sx={{ width: "100%", display: "block", bgcolor: "background.default" }}
              />
            )}
            <Stack spacing={1.25} sx={{ p: { xs: 2, md: 2.5 } }}>
              {notice && <Alert severity="success">{notice}</Alert>}
              {actionError && <Alert severity="error">{actionError}</Alert>}
              {extracted.title ? (
                <Typography variant="h4" sx={{ fontWeight: 900, color: "text.primary", fontSize: { xs: 26, md: 32 } }}>
                  {extracted.title}
                </Typography>
              ) : null}
              {canShowCalendarActions ? (
                <Typography sx={{ color: "text.primary", fontWeight: 800 }}>
                  {[dateLabel(extracted.date), clockLabel(formatDisplayTime(extracted.startTime))].filter(Boolean).join(" at ")}
                </Typography>
              ) : null}
              {showLocation && extracted.location && (
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: "text.secondary",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={extracted.location}
                  >
                    {extracted.location}
                  </Typography>
                  {directionsUrl && (
                    <Tooltip title="Directions">
                      <IconButton
                        component="a"
                        href={directionsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Directions"
                        size="small"
                        sx={{
                          width: 36,
                          height: 36,
                          border: 1, borderColor: "divider",
                          borderRadius: 1,
                          color: "text.primary",
                          flexShrink: 0,
                        }}
                      >
                        <DirectionsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              )}
              {customLinks.length > 0 && (
                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.25, bgcolor: "background.default" }}>
                  {customLinksTitle && (
                    <Typography sx={{ color: "text.primary", fontWeight: 900, mb: 1 }}>
                      {customLinksTitle}
                    </Typography>
                  )}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" useFlexGap>
                    {customLinks.map((link, index) => (
                      <Button
                        key={`${link.url}-${index}`}
                        component="a"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        startIcon={<LinkIcon />}
                        endIcon={<OpenInNewIcon />}
                        sx={{
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          "&:hover": { bgcolor: "primary.dark" },
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" useFlexGap sx={{ pt: 1 }}>
                {canShowCalendarActions && (
                  <>
                    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 190 } }}>
                      <InputLabel id="invite-reminder-label">Reminder</InputLabel>
                      <Select
                        labelId="invite-reminder-label"
                        label="Reminder"
                        value={reminderMinutes}
                        onChange={(event) => setReminderMinutes(Number(event.target.value))}
                      >
                        {REMINDER_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      onClick={saveToTimely365}
                      disabled={saving}
                      variant="contained"
                      startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <CalendarMonthIcon />}
                      sx={{ bgcolor: "primary.main", minHeight: 40 }}
                    >
                      Save to Timely365
                    </Button>
                    <Tooltip title={googleUrl ? "Add to Google Calendar" : "Date and time are needed for Google Calendar"}>
                      <span>
                        <Button
                          component={googleUrl ? "a" : "button"}
                          href={googleUrl || undefined}
                          target={googleUrl ? "_blank" : undefined}
                          rel={googleUrl ? "noopener noreferrer" : undefined}
                          disabled={!googleUrl}
                          variant="outlined"
                          startIcon={<GoogleIcon />}
                        >
                          Add to Google Calendar
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
                {canShareInvite && (
                  <Button variant="outlined" startIcon={<ShareIcon />} onClick={shareInvite}>
                    Share
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
}


